import { GalaxyConfig, StarData, StarPosition } from 'types/galaxy';

const K = 8;
const MAX_ITER = 12;

export const calculateClustersPositions = (
  starsData: StarData[],
  _config: GalaxyConfig
): StarPosition[] => {
  const count = starsData.length;
  const positions: StarPosition[] = new Array(count);

  // Normalize features to [0, 1]
  let minPnl = Infinity,
    maxPnl = -Infinity;
  let minRoi = Infinity,
    maxRoi = -Infinity;
  let minTrades = Infinity,
    maxTrades = -Infinity;

  starsData.forEach((t) => {
    if (t.pnl < minPnl) minPnl = t.pnl;
    if (t.pnl > maxPnl) maxPnl = t.pnl;
    if (t.roi < minRoi) minRoi = t.roi;
    if (t.roi > maxRoi) maxRoi = t.roi;
    if (t.totalTrades < minTrades) minTrades = t.totalTrades;
    if (t.totalTrades > maxTrades) maxTrades = t.totalTrades;
  });

  const rangePnl = maxPnl - minPnl || 1;
  const rangeRoi = maxRoi - minRoi || 1;
  const rangeTrades = maxTrades - minTrades || 1;

  // Feature vectors
  const features = starsData.map((t) => [
    (t.pnl - minPnl) / rangePnl,
    (t.roi - minRoi) / rangeRoi,
    (t.totalTrades - minTrades) / rangeTrades,
  ]);

  // Init centroids with k-means++ style
  const centroids: number[][] = [];
  centroids.push(features[Math.floor(Math.random() * count)].slice());
  for (let c = 1; c < K; c++) {
    let bestDist = -1,
      bestIdx = 0;
    for (let i = 0; i < count; i++) {
      let minD = Infinity;
      for (const cent of centroids) {
        const d = features[i].reduce((sum, v, dim) => sum + (v - cent[dim]) ** 2, 0);
        if (d < minD) minD = d;
      }
      if (minD > bestDist) {
        bestDist = minD;
        bestIdx = i;
      }
    }
    centroids.push(features[bestIdx].slice());
  }

  // K-means iterations
  const assignments = new Int32Array(count);
  for (let iter = 0; iter < MAX_ITER; iter++) {
    // Assign
    for (let i = 0; i < count; i++) {
      let minD = Infinity,
        minC = 0;
      for (let c = 0; c < K; c++) {
        const d = features[i].reduce((sum, v, dim) => sum + (v - centroids[c][dim]) ** 2, 0);
        if (d < minD) {
          minD = d;
          minC = c;
        }
      }
      assignments[i] = minC;
    }
    // Update centroids
    const sums = Array.from({ length: K }, () => [0, 0, 0]);
    const counts = new Int32Array(K);
    for (let i = 0; i < count; i++) {
      const c = assignments[i];
      counts[c]++;
      for (let d = 0; d < 3; d++) sums[c][d] += features[i][d];
    }
    for (let c = 0; c < K; c++) {
      if (counts[c] === 0) continue;
      for (let d = 0; d < 3; d++) centroids[c][d] = sums[c][d] / counts[c];
    }
  }

  // Place clusters in a ring around the center
  const clusterRadius = 45;
  const clusterCenters: { x: number; z: number }[] = [];
  for (let c = 0; c < K; c++) {
    const angle = (c / K) * Math.PI * 2;
    clusterCenters.push({
      x: Math.cos(angle) * clusterRadius,
      z: Math.sin(angle) * clusterRadius,
    });
  }

  // Group by cluster
  const clusterIndices: number[][] = Array.from({ length: K }, () => []);
  for (let i = 0; i < count; i++) {
    clusterIndices[assignments[i]].push(i);
  }

  const goldenAngle = Math.PI * (3 - Math.sqrt(5));

  clusterIndices.forEach((indices, c) => {
    const center = clusterCenters[c];
    const clusterCount = indices.length;
    const localRadius = Math.min(18, 4 + Math.sqrt(clusterCount) * 0.4);

    indices.forEach((idx, j) => {
      const a = j * goldenAngle;
      const r = localRadius * Math.sqrt(j / Math.max(clusterCount, 1)) + (Math.random() - 0.5) * 2;
      const y = (Math.random() - 0.5) * (localRadius * 0.5);

      positions[idx] = {
        x: center.x + Math.cos(a) * r,
        y,
        z: center.z + Math.sin(a) * r,
      };
    });
  });

  return positions;
};
